import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { format, addDays } from 'date-fns';

import FormGroup from '../../../../components/global/FormGroup';
import Button from '../../../../components/global/Button';
import { getErrorString } from '../../../../util/normalize-errors';
import Alert from '../../../../components/global/Alert';
import { sentryCaptureMessage } from '../../../../util/sentry';
import {
  formatNumber,
  formatPriceForDisplay,
} from '../../../../util/format-money';
import FaceMatchingIcon from '../../../../components/icons/FaceMatchingIcon';
import PhotosIcon from '../../../../components/icons/PhotosIcon';
import { DISCOUNT_MODE } from '../../../../util/promo-codes';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import PriceDisplay from '../../../../components/global/PriceDisplay';
import { getPhotoManagerSubscriptionPrice } from '../helpers';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';

const propTypes = {
  className: PropTypes.string,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  photoManagerSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ),
  photoFinderCreditItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  ),
  supportText: PropTypes.node,
  accountPromoCode: PropTypes.shape({
    discount: PropTypes.shape({
      mode: PropTypes.oneOf(Object.values(DISCOUNT_MODE)),
      value: PropTypes.number,
    }),
  }),
  skipPhotoFinder: PropTypes.bool,
  skipFreeTrial: PropTypes.bool,
  isWaldoProFreeRegister: PropTypes.bool,
};

const CARD_ELEMENT_OPTIONS = {
  iconStyle: 'solid',
  style: {
    base: {
      iconColor: '#969ea0',
      color: '#3c4c51',
      fontWeight: 800,
      fontFamily: 'Muli, sans-serif',
      fontSize: '16px',
      fontSmoothing: 'antialiased',

      ':-webkit-autofill': {
        color: '#fce883',
      },
      '::placeholder': {
        color: '#969ea0',
        fontWeight: 800,
      },
    },
    invalid: {
      iconColor: '#ea3770',
      color: '#ea3770',
    },
  },
};

const RegisterCheckoutCart = ({
  className,
  values,
  submitting,
  handleSubmit,
  handleChange,
  photoManagerSubscriptionItems,
  photoFinderCreditItems,
  supportText,
  accountPromoCode,
  skipPhotoFinder,
  skipFreeTrial,
  isWaldoProFreeRegister,
}) => {
  const classes = classNames({
    [className]: !!className,
    'view-register-checkout-form': true,
  });
  const stripe = useStripe();
  const elements = useElements();
  const [name, setName] = useState(
    `${values.first_name} ${values.last_name}`.trim(),
  );
  const [email, setEmail] = useState(values.email_address);
  const [loading, setLoading] = useState(false);
  const [stripeError, setStripeError] = useState('');

  const photoManagerSubscriptionItem = photoManagerSubscriptionItems.find(
    subscriptionItem => subscriptionItem.id === values.plan_id,
  );
  const photoFinderCreditsItem = photoFinderCreditItems.find(
    subscriptionItem => subscriptionItem.id === values.tier_id,
  );

  const photoManagerSubscriptionPrice = getPhotoManagerSubscriptionPrice({
    accountPromoCode,
    photoManagerSubscriptionItem,
  });
  const photoFinderSubscriptionPrice =
    photoFinderCreditsItem?.price?.value ?? 0;

  const isFutureCharge = (() => {
    if (photoFinderCreditsItem) {
      return false;
    }

    if (isWaldoProFreeRegister) {
      return true;
    }

    if (accountPromoCode?.trial_days) {
      return true;
    }

    if (!skipFreeTrial && !!photoManagerSubscriptionItem?.trial_days) {
      return true;
    }

    return false;
  })();

  const trialDays =
    accountPromoCode?.trial_days ?? photoManagerSubscriptionItem?.trial_days;

  const finalCharge = formatNumber(
    formatPriceForDisplay(
      photoFinderSubscriptionPrice + photoManagerSubscriptionPrice,
      true,
    ),
  );

  const buttonLabel = (() => {
    if (submitting || loading) {
      return 'Processing...';
    }

    if (isWaldoProFreeRegister) {
      return 'Start Trial';
    }

    if (isFutureCharge && trialDays) {
      return `Start ${trialDays}-day free trial`;
    }

    return 'Complete purchase';
  })();

  const onPayment = async event => {
    event.preventDefault();

    if (loading || submitting) {
      return;
    }

    setLoading(true);
    setStripeError('');

    try {
      const { error, token } = await stripe.createToken(
        elements.getElement(CardElement),
        {
          name,
        },
      );

      if (error) {
        throw Error(error.message);
      }

      handleChange({
        target: {
          name: 'stripe_token',
          value: token,
        },
      });

      handleSubmit();
    } catch (error) {
      setStripeError(getErrorString(error));
      sentryCaptureMessage('PRO SIGNUP - CC Capture Error ', {
        level: 'error',
        extra: {
          error,
          email: values.email,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="view-register-photo-finder-upsell-checkout-wrapper">
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center mr-2">
            <div className="photo-manager-subscription-title-icon">
              <PhotosIcon color="white" size={14} />
            </div>
            <p className="text-xs text-dark mb-0">
              {photoManagerSubscriptionItem?.billing_period ===
              PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                ? 'Monthly'
                : 'Annual'}{' '}
              Photo Manager Subscription{' '}
              {isWaldoProFreeRegister && `(1-year Free Trial)`}
              {!isWaldoProFreeRegister &&
                isFutureCharge &&
                trialDays &&
                `(${trialDays}-day Free Trial)`}
            </p>
          </div>
          <div
            className="view-register-pill"
            data-for="discount-tooltip"
            data-placement="top"
            data-effect="solid"
            data-tip
          >
            <PriceDisplay
              title="Price"
              price={photoManagerSubscriptionPrice}
              oldPrice={photoManagerSubscriptionItem?.price.value}
              showDiscount
              animatedStrikethrough
            />
            {photoManagerSubscriptionItem && (
              <>
                {' '}
                /{' '}
                {photoManagerSubscriptionItem?.billing_period ===
                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY
                  ? 'mo'
                  : 'yr'}
              </>
            )}
            {!!values.promo_code &&
              [
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
                ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
              ].includes(accountPromoCode?.type) && (
                <ReactTooltip id="discount-tooltip" className="tooltip">
                  Your promo code has been applied!
                </ReactTooltip>
              )}
          </div>
        </div>
        {!skipPhotoFinder && (
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center mr-2">
              <div className="photo-finder-subscription-title-icon">
                <FaceMatchingIcon color="white" size={14} />
              </div>
              <p className="text-xs text-dark mb-0">Photo Finder</p>
            </div>

            <div className="view-register-pill">
              {photoFinderCreditsItem ? (
                <>
                  <PriceDisplay
                    title="Price"
                    price={photoFinderSubscriptionPrice}
                    oldPrice={photoFinderCreditsItem?.price.value}
                    animatedStrikethrough
                    showDiscount
                  />
                </>
              ) : (
                <>Free Trial</>
              )}
            </div>
          </div>
        )}
        <hr className="w-100 my-2" />
        <div className="d-flex justify-content-between align-items-center">
          <p className="text-extra-bold text-sm text-dark">
            {isFutureCharge ? (
              <>
                <span className="text-extra-bold text-sm text-primary">
                  ${finalCharge}
                </span>{' '}
                will be charged on{' '}
                {format(
                  addDays(Date.now(), isWaldoProFreeRegister ? 365 : trialDays),
                  'MMMM d, yyyy',
                )}{' '}
                unless cancelled prior.
              </>
            ) : (
              "Today's charge"
            )}
          </p>
          {!isFutureCharge && (
            <p className="text-extra-bold text-sm text-primary">
              ${finalCharge}
            </p>
          )}
        </div>
      </div>
      <form className={classes} onSubmit={onPayment}>
        <fieldset className="view-register-checkout-fieldset">
          <FormGroup
            label="Name"
            labelSrOnly
            id="card-name"
            name="name"
            placeholder="Name on card"
            required
            value={name}
            onChange={event => {
              const { target } = event;
              const { value } = target;
              setName(value);
            }}
            disabled={submitting || loading}
          />
        </fieldset>
        <fieldset className="view-register-checkout-fieldset">
          <FormGroup
            label="Email"
            labelSrOnly
            id="card-email"
            name="email"
            placeholder="Email"
            type="email"
            required
            readOnly
            value={email}
            onChange={event => {
              const { target } = event;
              const { value } = target;
              setEmail(value);
            }}
            disabled={submitting || loading}
          />
        </fieldset>

        <fieldset className="view-register-checkout-fieldset view-register-checkout-fieldset-credit-card">
          <CardElement
            options={CARD_ELEMENT_OPTIONS}
            disabled={submitting || loading}
          />
        </fieldset>

        {values.promo_code && (
          <fieldset className="view-register-checkout-fieldset view-register-promo-box p-2 mb-4">
            <FormGroup
              id="promo_code"
              name="promo_code"
              label="Promo Code"
              labelSrOnly
              placeholder="Have a promo code?"
              type="text"
              className="mb-1"
              readOnly
              defaultValue={values.promo_code}
            />

            <p className="mb-0 text-sm text-dark text-bold">
              Your promo code has been applied! You will be saving on a Photo
              Manager Subscription!
            </p>
          </fieldset>
        )}

        {stripeError && (
          <Alert type="danger" className="mt-3">
            {stripeError}
          </Alert>
        )}

        <div className="mt-3 text-center">
          <Button
            id="pro-reg-btn-form-submit"
            className="pro-reg-btn-form-submit"
            type="submit"
            disabled={submitting || loading}
          >
            {buttonLabel}
          </Button>
        </div>
        {isWaldoProFreeRegister && (
          <div className="mt-2">
            <Alert type="info" className="w-100">
              Photo manager is free for first year and forever for all accounts
              with $5k or more in sales in the last year.
            </Alert>
          </div>
        )}
        {supportText}
      </form>
    </div>
  );
};

RegisterCheckoutCart.propTypes = propTypes;

export default RegisterCheckoutCart;
